import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import "./baseStyles.css";
import "./UploadCampaignDataPage.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axios from "axios";

const UploadCampaignDataPage = () => {
  const navigate = useNavigate();
  const [cognitoUserId, setCognitoUserId] = useState("");
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [infoMessages, setInfoMessages] = useState([]);
  const [previewLimit] = useState(5);
  const [selectedCampaignType, setSelectedCampaignType] = useState("PersonalReminders");
  const [userCampaigns, setUserCampaigns] = useState([]);

  const allowedNowUsers = ["24c8d4f8-e0d1-700b-91fe-340cedc4d1fb", "dummy"];

  useEffect(() => {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) return navigate("/login");

    try {
      const decoded = jwtDecode(idToken);
      if (decoded.exp * 1000 < Date.now()) throw new Error("Token expired");
      setCognitoUserId(decoded.sub);
      fetchAllCampaigns(decoded.sub);
    } catch (e) {
      localStorage.removeItem("idToken");
      navigate("/login");
    }
  }, []);

  const fetchAllCampaigns = async (userid) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}`, {
        params: { operation: "fetch_all", userid },
        headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
      });
      const result = Array.isArray(res.data) ? res.data : [res.data];
      setUserCampaigns(result);
    } catch (err) {
      console.error("Error fetching campaigns:", err);
    }
  };

  const handleCampaignTypeChange = (e) => {
    setSelectedCampaignType(e.target.value);
  };

  const handleFileUploadToBrowser = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setParsedData(results.data);
        validateRows(results.data);
      },
      error: (err) => {
        console.error("CSV parse error:", err.message);
      },
    });
  };

  const handleUploadToDB = async () => {
    if (parsedData.length === 0 || validationErrors.length > 0) {
      alert("Fix errors before uploading.");
      return;
    }

    const token = localStorage.getItem("idToken");
    const campaignName = parsedData[0].campaignName?.trim();
    if (!campaignName) {
      alert("Campaign name is required.");
      return;
    }


    const realName = selectedCampaignType === "PersonalReminders"
      ? `${cognitoUserId}_${campaignName}`
      : campaignName;




    const alreadyExists = userCampaigns.find(
      (c) =>
        c.campaignName === campaignName ||
        c.realCampaignName === realName ||
        c.campaignListTemplateName === campaignName
    );

    switch (selectedCampaignType) {
      case "PersonalReminders":
        if (!alreadyExists) {
          try {
            await addCampaignToUserList(campaignName, realName, selectedCampaignType);
            setInfoMessages((prev) => [...prev, `✅ Campaign "${campaignName}" was added.`]);
            await fetchAllCampaigns(cognitoUserId);
          } catch {
            setInfoMessages((prev) => [...prev, `❌ Failed to add campaign. Upload cancelled.`]);
            return;
          }
        } else {
          setInfoMessages((prev) => [...prev, `ℹ️ Campaign "${campaignName}" already exists.`]);
        }
        await uploadReminderItems(transformRows(parsedData), token);
        break;

      case "CommercialReminders":
        // ✅ No list creation, just upload
        await uploadReminderItems(transformRows(parsedData), token);
        break;

      case "BigQuestion":
        setInfoMessages((prev) => [...prev, "ℹ️ BigQuestion upload not implemented yet."]);
        break;

      case "Favorites":
        setInfoMessages((prev) => [...prev, "ℹ️ Favorites upload not implemented yet."]);
        break;

      default:
        setInfoMessages((prev) => [...prev, "❌ Unknown campaign type."]);
    }
  };

  const transformRows = (rows) =>
    rows.map((row, index) => {
      const name = row.campaignName?.trim() || "Unnamed";
      return {
        ...row,
        campaignType: selectedCampaignType,
        userid: cognitoUserId,
        realCampaignName: `${cognitoUserId}_${name}`,
        campaignItemsId: row.campaignItemsId || index + 1
      };
    });

  const addCampaignToUserList = async (campaignName, realName, campaignType) => {
    const payload = {
      operation: "add_list_item",
      userid: cognitoUserId,
      campaignName,
      campaignListRealCampaignName: realName,
      campaignListCampaignType: campaignType,
      campaignListCommMethod: "email",
      campaignListSendWhen: "readNext",
      campaignListNextSeq: 1,
      campaignListReminderSchedule: "daily_8am",
      campaignListReminderDays: "7"
    };

    await axios.post(`${process.env.REACT_APP_API_URL}`, payload, {
      headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` }
    });
  };

  const uploadReminderItems = async (rows, token) => {
    const friendlyCampaignName = rows[0].campaignName?.trim();
    const userId = rows[0].userid;

    const finalName =
      selectedCampaignType === "PersonalReminders"
        ? `${userId}_${friendlyCampaignName}`
        : friendlyCampaignName;


    try {
      const existingItems = await fetchExistingItems(friendlyCampaignName, userId, token);
      const existingIds = new Set(existingItems.map(item => parseInt(item.campaignItemsId)));

      const results = await Promise.allSettled(
        rows.map((row, index) => {
          const itemId = parseInt(row.campaignItemsId || index + 1);
          const payload = {
            operation: "add_campaign_bulk_item",
            userid: userId,
            campaignName: finalName,
            campaignItemsId: itemId,
            campaignItemsType: row.campaignItemsType || selectedCampaignType,
            campaignItemsAuthor: row.campaignItemsAuthor || "",
            campaignItemsName: row.campaignItemsName || "",
            campaignItemsText: row.campaignItemsText || "",
            campaignItemsURL1: row.campaignItemsURL1 || "",
          };

          return axios.post(`${process.env.REACT_APP_API_URL}`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
        })
      );

      const successCount = results.filter(r => r.status === "fulfilled").length;
      const failureCount = results.length - successCount;

      setInfoMessages((prev) => [
        ...prev,
        `✅ ${successCount} items uploaded.`,
        ...(failureCount > 0 ? [`⚠️ ${failureCount} items failed.`] : [])
      ]);
    } catch (err) {
      console.error("❌ Upload failed", err);
      setInfoMessages((prev) => [...prev, "❌ Upload failed."]);
    }
  };

  const fetchExistingItems = async (campaignName, userId, token) => {
    try {
      const payload = {
        operation: "fetch_campaign_items",
        userid: userId,
        campaignName,
      };
      const res = await axios.post(`${process.env.REACT_APP_API_URL}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      console.error("❌ Failed to fetch existing items:", err);
      return [];
    }
  };

  const validateRows = (rows) => {
    const errors = [];
    const info = [];

    const campaignNames = rows.map(row => row.campaignName?.trim()).filter(Boolean);
    const uniqueNames = [...new Set(campaignNames)];

    if (uniqueNames.length !== 1) {
      info.push(`❌ Multiple campaignNames found: ${uniqueNames.join(", ")}`);
    } else {
      info.push(`✅ Campaign name is consistent: "${uniqueNames[0]}"`);
    }

    rows.forEach((row, index) => {
      const missing = [];
      if (selectedCampaignType === "CommercialReminders") {
        ["campaignItemsId", "campaignItemsText"].forEach(f => {
          if (!row[f]) missing.push(f);
        });
      }

      if (selectedCampaignType === "PersonalReminders") {
        ["campaignItemsId", "campaignItemsName", "campaignItemsText"].forEach(f => {
          if (!row[f]) missing.push(f);
        });
      }

      if (missing.length > 0) {
        errors.push({ row: index + 1, missingFields: missing });
      }
    });

    setValidationErrors(errors);
    setInfoMessages(info);
  };

  return (
    <div className="upload-container">
      <h2>📁 Upload Campaign Data</h2>

      <div className="form-section">
        <label><strong>Select Campaign Type:</strong></label>
        <select value={selectedCampaignType} onChange={handleCampaignTypeChange}>
          <option value="PersonalReminders">PersonalReminders</option>
          {allowedNowUsers.includes(cognitoUserId) && (
            <>
              <option value="CommercialReminders">CommercialReminders</option>
              <option value="BigQuestion">BigQuestion</option>
              <option value="Favorites">Favorites</option>
            </>
          )}
        </select>
      </div>


      <div className="form-section">
        <label><strong>Choose File:</strong></label>
        <input type="file" accept=".csv" onChange={handleFileUploadToBrowser} />
      </div>


      {parsedData.length > 0 && (
        <div className="preview-box">
          <h3>📄 Preview (first {previewLimit} rows):</h3>
          <table className="preview-table">
            <thead>
              <tr>{Object.keys(parsedData[0]).map((key) => <th key={key}>{key}</th>)}</tr>
            </thead>
            <tbody>
              {parsedData.slice(0, previewLimit).map((row, idx) => (
                <tr key={idx}>
                  {Object.values(row).map((val, j) => <td key={j}>{val}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {infoMessages.length > 0 && (
        <div className="info-list-container">
          <ul className="info-list">
            {infoMessages.map((msg, idx) => <li key={idx}>{msg}</li>)}
          </ul>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="error-list-container">
          <ul className="error-list">
            {validationErrors.map((err, idx) => (
              <li key={idx}>Row {err.row}: {err.missingFields.join(", ")}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-section">
        <button
          className="submit-button"
          onClick={handleUploadToDB}
          disabled={validationErrors.length > 0 || parsedData.length === 0}
        >
          🚀 Upload Valid Rows
        </button>
      </div>
    </div>
  );
};

export default UploadCampaignDataPage;
